// src/oura/newscan/useSiteRead.ts
//
// The New scan flow before a draft exists: the website field, the read of the
// site (with its progress ticks) and the Business form seeded from it. Kept
// for the tab in sessionStorage, so a refresh on Business keeps the read.
// A resumed draft seeds the form, so the Business step has its values.
//
// Composed by useScanFlowState (ScanFlowContext.ts).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ScanDraft, ScanDraftBusiness, ScanDraftDetected } from "@service/aeo/scanDraftTypes";
import { readSite as readSiteService } from "@service/aeo/scanDraft";
import { FRAME, READ_PAGES } from "./copy";
import { normalizeDomain } from "./domain";
import { formFromDetected } from "./flowSteps";

export interface ScanFlowSite {
    /** What is typed in the website field, as typed. */
    domain: string;
    /** `domain` normalised (bare host, or host + path on Wix). "" when it cannot be parsed. */
    normalized: string;
    detected: ScanDraftDetected | null;
    reading: boolean;
    /** Pages done in the progress list while reading; `detected.pages.length` once read. */
    readDone: number;
    /** The read's failure sentence, or null. */
    error: string | null;
}

const READ_TICK_MS = 1_100;

/* ------------------------------------------------------------------------- *
 * Before a draft: kept for the tab, so a refresh on Business keeps the read
 * ------------------------------------------------------------------------- */

const PRE_KEY_PREFIX = "oura.newscan.pre.v1";

interface PreDraft {
    domain: string;
    detected: ScanDraftDetected | null;
    form: ScanDraftBusiness | null;
}

/**
 * One key per signed-in user and workspace: sessionStorage outlives a logout in the
 * same tab, so a second account must not find the first one's website and form.
 * No scope (the profile has not loaded) means no key, and nothing is kept.
 */
function preKey(storageScope: string): string | null {
    return storageScope ? `${PRE_KEY_PREFIX}.${encodeURIComponent(storageScope)}` : null;
}

function loadPre(key: string | null): PreDraft | null {
    if (!key) return null;
    try {
        const raw = window.sessionStorage.getItem(key);
        if (!raw) return null;
        const v = JSON.parse(raw) as Partial<PreDraft> | null;
        if (!v || typeof v.domain !== "string") return null;
        return { domain: v.domain, detected: v.detected ?? null, form: v.form ?? null };
    } catch {
        return null;
    }
}

function savePre(key: string | null, v: PreDraft | null): void {
    if (!key) return;
    try {
        if (v) window.sessionStorage.setItem(key, JSON.stringify(v));
        else window.sessionStorage.removeItem(key);
    } catch {
        // Storage off (private window): the flow still works, a refresh starts over.
    }
}

/** Forget the tab's pre-draft state for this user and workspace: a draft now holds the work. */
export function clearPreDraft(storageScope: string): void {
    savePre(preKey(storageScope), null);
}

/* ------------------------------------------------------------------------- *
 * The hook
 * ------------------------------------------------------------------------- */

export interface SiteReadSetup {
    routeKind: "new" | "business";
    draftId: string | null;
    draft: ScanDraft | null;
    /** The signed-in user and workspace the kept pre-draft state belongs to, e.g. `${userId}:${companyId}`. */
    storageScope: string;
}

export interface SiteRead {
    site: ScanFlowSite;
    setDomain(d: string): void;
    readSite(): Promise<void>;
    form: ScanDraftBusiness | null;
    setForm(next: ScanDraftBusiness): void;
}

export function useSiteRead({ routeKind, draftId, draft, storageScope }: SiteReadSetup): SiteRead {
    const storageKey = preKey(storageScope);
    const restored = useRef<PreDraft | null>(routeKind === "new" && !draftId ? loadPre(storageKey) : null);
    const [domain, setDomainState] = useState(restored.current?.domain ?? "");
    const [detected, setDetected] = useState<ScanDraftDetected | null>(restored.current?.detected ?? null);
    const [form, setFormState] = useState<ScanDraftBusiness | null>(restored.current?.form ?? null);
    // The key the state above was loaded from: nothing is written to a key before it has been read,
    // so a profile that arrives late finds the saved state instead of replacing it with an empty one.
    const [hydratedKey, setHydratedKey] = useState<string | null>(routeKind === "new" && !draftId ? storageKey : null);
    const [reading, setReading] = useState(false);
    const [readDone, setReadDone] = useState(0);
    const [readError, setReadError] = useState<string | null>(null);
    const normalized = normalizeDomain(domain);
    const readSeq = useRef(0);

    useEffect(() => {
        if (routeKind !== "new" || draftId || !storageKey || hydratedKey === storageKey) return;
        const saved = loadPre(storageKey);
        const hasPreHydrationInput = Boolean(domain || detected || form);
        if (saved && (hydratedKey !== null || !hasPreHydrationInput)) {
            setDomainState(saved.domain);
            setDetected(saved.detected);
            setFormState(saved.form);
        } else if (!saved && hydratedKey !== null) {
            // Another user or workspace's key: it must not inherit the previous scope's state.
            setDomainState("");
            setDetected(null);
            setFormState(null);
        }
        // The first real key never overwrites what was entered while the profile loaded: with
        // nothing saved it is kept, and it wins over an older saved pre-draft too (Jack, #4286/#4289).
        setHydratedKey(storageKey);
    }, [routeKind, draftId, storageKey, hydratedKey, domain, detected, form]);

    useEffect(() => {
        if (routeKind !== "new" || draftId || !storageKey || hydratedKey !== storageKey) return;
        savePre(storageKey, domain || detected || form ? { domain, detected, form } : null);
    }, [routeKind, draftId, storageKey, hydratedKey, domain, detected, form]);

    // The form and the read belong to one draft: moving from draft A to draft B drops A's,
    // so B is seeded from its own business and never shows or saves A's (Jack, #4286).
    // The first draft after the pre-draft read keeps that read: it is the same site.
    const formDraftId = useRef<string | null>(draftId);
    useEffect(() => {
        if (!draftId || formDraftId.current === draftId) {
            if (draftId) formDraftId.current = draftId;
            return;
        }
        const switched = formDraftId.current !== null;
        formDraftId.current = draftId;
        if (!switched) return;
        readSeq.current += 1;
        setFormState(null);
        setDetected(null);
        setReadError(null);
        setReadDone(0);
        setReading(false);
    }, [draftId]);

    // A resumed draft seeds the form, so the Business step has its values.
    useEffect(() => {
        if (draft && draft.id === draftId && form === null) setFormState(draft.business);
    }, [draft, draftId, form]);

    const setDomain = useCallback((d: string) => {
        setDomainState((was) => {
            // A different site: the earlier read and form belong to the old one.
            // (Search Console resets on its own when the site changes: useGscState.)
            if (normalizeDomain(d) !== normalizeDomain(was)) {
                readSeq.current += 1;
                setDetected(null);
                setFormState(null);
                setReading(false);
                setReadDone(0);
            }
            return d;
        });
        setReadError(null);
    }, []);

    const setForm = useCallback((next: ScanDraftBusiness) => setFormState(next), []);

    const readSite = useCallback(async () => {
        const target = normalized;
        if (!target) return;
        const seq = ++readSeq.current;
        setReading(true);
        setReadDone(0);
        setReadError(null);
        setDetected(null);
        const ticker = window.setInterval(() => {
            setReadDone((n) => Math.min(n + 1, READ_PAGES.length - 1));
        }, READ_TICK_MS);
        const res = await readSiteService(target);
        window.clearInterval(ticker);
        if (seq !== readSeq.current) return;
        setReading(false);
        if (!res.ok) {
            setReadError(res.kind === "network" ? FRAME.network : res.message);
            return;
        }
        setDetected(res.data);
        setReadDone(res.data.pages.length);
        setFormState(formFromDetected(res.data));
    }, [normalized]);

    const site = useMemo<ScanFlowSite>(
        () => ({ domain, normalized, detected, reading, readDone, error: readError }),
        [domain, normalized, detected, reading, readDone, readError],
    );

    return { site, setDomain, readSite, form, setForm };
}
